import React, { Component } from 'react'
import PropTypes from 'prop-types'
import './styles.styl'
import play from '../../assets/images/global-play.svg'
import pause from '../../assets/images/global-pause.svg'
import replay from '../../assets/images/global-replay.svg'

/**
 * Newer version of Slideshow that supports iOS Safari
 * and only supports slideshows with audio.
 *
 * Note: some props may have been added via redux 'connect', e.g. the 'audioEnabled' prop
 */
class Slideshow extends Component {
  constructor(props) {
    super(props)

    // Instance variables
    this.audioPlayersLookup = {}
    this.audioLastTimestampLookup = {}
    this.audioContainer = null
    this.invocationContextId = 0
    this.currentAudioIndex = 0

    // Refs
    this.setAudioContainerRef = el => {
      this.audioContainer = el
      this.setState({ audioContainerReady: true })
    }

    // State
    this.state = {
      audioLoaded: false,
      audioConfigured: false,
      audioContainerReady: false,
      audioReadyCounter: 0,
      completedDuration: 0,
      currentSlide: 0,
      hasPlayedThrough: false,
      paused: true,
      totalDuration: this.getDuration(this.props.slides),
    }

    this.updateDuration(props.slides)

    this.audioReadyListener = this.audioReadyListener.bind(this)
  }

  audioReadyListener(event) {
    const audio = event.target
    audio.removeEventListener('canplaythrough', this.audioReadyListener)
    this.setState({ audioReadyCounter: this.state.audioReadyCounter + 1 })
  }

  componentDidUpdate(prevProps, prevState) {
    // Only update duration if slides differ
    if (
      prevProps.slides.length !== this.props.slides.length ||
      !this.props.slides.every(
        (slide, index) => prevProps.slides[index].duration === slide.duration
      )
    ) {
      this.updateDuration(this.props.slides)
    }

    // This next block creates the audio elements, and listens for when
    // all the audio elements have triggered the 'canplaythrough' event.
    if (!this.state.audioConfigured && this.state.audioContainerReady) {
      const voiceovers = this.props.slides.map(slide => slide.vo.sources)
      this.audioPlayersLookup.current = {} // Reset Lookup
      voiceovers.forEach((sources, index) => {
        const audio = new Audio()
        sources.forEach(source => {
          const sourceElement = document.createElement('source')
          sourceElement.setAttribute('type', 'audio/mpeg')
          sourceElement.setAttribute('src', source.src)
          audio.appendChild(sourceElement)
        })
        // Add listener to check when audio file is loaded, i.e. 'canplaythrough' listener
        audio.addEventListener('canplaythrough', this.audioReadyListener)
        // This 'load()' call is needed for iOS Safari to trigger the 'canplaythrough' event.
        audio.load()
        this.audioPlayersLookup[`${index}`] = audio
        this.audioLastTimestampLookup[`${index}`] = 0
        this.audioContainer.appendChild(audio)
      })
      // Set audioConfigured to prevent redundant calls to this block.
      this.setState({ audioConfigured: true })
    }

    // If all the audio elements have loaded, update the 'audioReady' state so that the play button shows.
    if (
      prevState.audioReadyCounter !== this.state.audioReadyCounter &&
      this.state.audioReadyCounter >= this.props.slides.length
    ) {
      this.setState({ audioReady: true })
    }

    // Handle muting
    const currentAudio = this.audioPlayersLookup[`${this.currentAudioIndex}`]
    if (currentAudio) {
      if (this.props.audioEnabled) {
        currentAudio.muted = false
      } else {
        currentAudio.muted = true
      }
    }
  }

  getDuration = slides => {
    let totalDuration = 0
    slides.forEach(slide => {
      totalDuration += slide.duration
    })
    return totalDuration
  }

  updateDuration = slides => {
    const totalDuration = this.getDuration(slides)
    if (totalDuration !== this.state.totalDuration) {
      this.setState({
        totalDuration: totalDuration,
      })
    }
  }

  slideComplete = index => {
    const { currentSlide } = this.state
    const newCompletedDuration =
      this.state.completedDuration + this.props.slides[currentSlide].duration

    if (index !== currentSlide) {
      console.warn(
        'Slide ' +
          index +
          ' reported completion but Slideshow reports it is currently on slide ' +
          currentSlide
      )
    }

    if (index + 1 === this.props.slides.length) {
      this.setState({
        completedDuration: newCompletedDuration,
        paused: true,
        hasPlayedThrough: true,
      })
      this.props.onCompletion()
    } else {
      this.setState({
        completedDuration: newCompletedDuration,
        currentSlide: currentSlide + 1,
      })
    }
  }

  togglePause = () => {
    // Increment the invocationContext. That way if multiple toggles of play/pause
    // are made, then old event listeners will be ignored, since their invocation
    // context no longer matches the current value.
    this.invocationContextId += 1
    const currentAudio = this.audioPlayersLookup[`${this.currentAudioIndex}`]
    if (this.state.paused) {
      const invocationId = this.invocationContextId
      const playQueue = (
        playingAudio,
        invocationContext,
        slideshowInstance
      ) => {
        function endedListener() {
          // Avoid future calls to this same listener
          playingAudio.removeEventListener('ended', endedListener)
          // Only invoke if we are still in the same execution context.
          if (invocationContext === slideshowInstance.invocationContextId) {
            // Set the timestamp for this audio to zero since it has ended.
            slideshowInstance.audioLastTimestampLookup[
              `${slideshowInstance.currentAudioIndex}`
            ] = 0
            // Move on to the next audio
            slideshowInstance.currentAudioIndex += 1
            if (
              slideshowInstance.currentAudioIndex <
              slideshowInstance.props.slides.length
            ) {
              const nextAudio =
                slideshowInstance.audioPlayersLookup[
                  `${slideshowInstance.currentAudioIndex}`
                ]
              nextAudio.play()
              // RECURSIVE CALL - keep invoking until no more audio elements left -
              // or user presses pause, and this listener is then ignored due to invocation context.
              playQueue(nextAudio, invocationId, slideshowInstance)
            } else {
              slideshowInstance.currentAudioIndex = 0
            }
          }
        }
        playingAudio.addEventListener('ended', endedListener)
      }
      // For iOS Safari to work right, we need to send the cue to the last pause time:
      currentAudio.currentTime = this.audioLastTimestampLookup[
        `${this.currentAudioIndex}`
      ]
      currentAudio.play()
      playQueue(currentAudio, invocationId, this)
    } else {
      // was playing, lets pause it
      currentAudio.pause()
      // Set the player to the proper last timestamp // << Needed this for iOS
      this.audioLastTimestampLookup[`${this.currentAudioIndex}`] =
        currentAudio.currentTime + 0.05 // Added 0.05 seconds so it sounds better in iOS Safari.
    }
    if (this.state.hasPlayedThrough) {
      const newState = {
        currentSlide: 0,
        completedDuration: 0,
        paused: !this.state.paused,
        hasPlayedThrough: this.state.paused,
      }
      this.setState(newState)
    }

    this.setState({
      paused: !this.state.paused,
    })
  }

  determineButtonImage = () => {
    const { paused, hasPlayedThrough } = this.state
    if (hasPlayedThrough && paused) {
      return replay
    } else if (paused) {
      return play
    } else {
      return pause
    }
  }

  render() {
    const {
      audioReady,
      paused,
      completedDuration,
      totalDuration,
      currentSlide,
      hasPlayedThrough,
    } = this.state
    const completedFraction = completedDuration / totalDuration

    // TODO: Would be nice to show Comet loading spinner when waiting for canPlayThrough to turn true

    // TODO: Could be nice to set Slideshow up with its own timer so that the progress bar advances at a steady clip
    //       instead of only between slides.

    return (
      <div className="slideshow">
        {this.props.slides.map((slide, i) => {
          const {
            SlideType, // don't pass type through
            vo, // Don't pass voiceover through
            ...slidePassthroughProps
          } = slide

          const slideHidden = i !== currentSlide
          const slidePaused = paused || slideHidden
          const voClone = { ...vo }
          voClone.sources = []

          return (
            <SlideType
              {...slidePassthroughProps}
              key={i}
              index={i}
              vo={voClone}
              hidden={slideHidden}
              paused={slidePaused}
              onComplete={this.slideComplete}
              updateCanPlayThrough={() => {}}
              hasPlayedThrough={hasPlayedThrough}
            />
          )
        })}

        <div className="slideshow__progress-bar">
          <div
            className="slideshow__progress-bar__progress"
            style={{ transform: 'scaleX(' + completedFraction + ')' }}
          />
        </div>

        <div ref={this.setAudioContainerRef}></div>
        <div className="slideshow__controls">
          {audioReady && (
            <button
              aria-pressed={!paused}
              className="slideshow__controls__play-pause"
              onClick={this.togglePause}
            >
              <img src={this.determineButtonImage()} alt="" />

              <span className="comet-screenreader-only">Play Slideshow</span>
            </button>
          )}
        </div>
      </div>
    )
  }
}

Slideshow.defaultProps = {
  autoPlay: true,
}

Slideshow.propTypes = {
  autoPlay: PropTypes.bool,
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      SlideType: PropTypes.func,
      duration: PropTypes.number,
    })
  ),
}

export default Slideshow



// WEBPACK FOOTER //
// ../core/src/components/Slideshow/Slideshow.js